import { Component, ElementRef, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, Event, NavigationStart, NavigationEnd } from '@angular/router';
import { Observable, Subscription, interval } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Menu, TitleService, MenuService, AuthService, LanguageService, SelectListItem } from '@shared/public_api';
import { PasswordChangeComponent } from './password-change/password-change.component';
import { MatDialog } from '@angular/material/dialog';
import { CompanyRouteService, SnackbarFactory, ThemeService } from '@shared/services';
import { AppVersionService } from '@app/shared/services/app-version.service';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { MatSelectChange } from '@angular/material/select';

@Component({
  selector: 'app-nav-menu',
  templateUrl: './nav-menu.component.html',
  styleUrls: ['./nav-menu.component.scss'],
})
export class NavMenuComponent implements OnInit, OnDestroy {
  isAuthenticated = false;
  menu: Menu[] = [];
  moduleMenu: Menu[] = [];
  currentMenu: Menu = null;
  userLongName: string;
  loading = false;
  public moGenerationRunning: boolean = false;
  @ViewChild('moGenRunningStatusIcon')
  public moGenerationStatusIcon: ElementRef;
  languageList: SelectListItem[];
  isHandset$: Observable<boolean>;

  private appVerCheckSubscription: Subscription;
  private showUpdateNeededWarning: boolean = false;

  constructor(
    protected injector: Injector,
    private translate: TranslateService,
    private title: TitleService,
    private menuService: MenuService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private authService: AuthService,
    private dialog: MatDialog,
    private langService: LanguageService,
    private snackBar: SnackbarFactory,
    private appVersionService: AppVersionService,
    public companyRouteService: CompanyRouteService,
    public themeService: ThemeService,
  ) {
    this.companyRouteService.getCompanyIdsSelectList().subscribe({
      next: (list) => {
        this.companyRouteList = list;
      }
    });
  }

  companyRouteList: SelectListItem[] = [];

  onSelectionChanged(event: MatSelectChange) {
    let currentUrlParts = this.router.url.split('/').filter(r => r !== '').slice(1);
    let joinedRoute = '';
    if (currentUrlParts.length != 0) {
      joinedRoute = `/${currentUrlParts.join('/')}`
    }
    this.router.navigate([`/${event.value}${joinedRoute}`]).finally(() => {
      this.reloadPage();
    });
  }

  ngOnInit() {
    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) this.loading = true;
      if (event instanceof NavigationEnd) this.loading = false;
    });

    this.isHandset$ = this.breakpointObserver
      .observe([Breakpoints.Handset, Breakpoints.Small, Breakpoints.Medium])
      .pipe(map((result) => result.matches));

    this.title.resetTitle();

    this.authService.isAuthenticatedAsync().subscribe({
      next: isAuth => {
        if (isAuth) {
          this.isAuthenticated = true;
          this.userLongName = this.authService.userLongName;
          this.getMenus();
        }
      }
    })

    try {
      this.translate.get(this.authService.userLang);
      this.translate.use(this.authService.userLang);
    } catch (error) {
      this.translate.get(this.translate.defaultLang);
      this.translate.use(this.translate.defaultLang);
    }
    this.getSelectLists();

    //this.getLatestAppVersion();
    //this.appVerCheckSubscription = interval(2 * 5 * 1000).subscribe(() => {
    //  this.getLatestAppVersion();
    //});
  }

  private getLatestAppVersion() {
    this.appVersionService.getAppVersion()
      .subscribe({
        next: res => {
          if (res['bulkColumn'] == localStorage.getItem("appVersionHash")) {
            console.log("The app is up to date.")
            this.showUpdateNeededWarning = false
          }
          else {
            console.log("App needs an update.")
            this.showUpdateNeededWarning = true
          };
        }
      })
  }

  private getMenus() {
    this.menu = [];
    this.menuService.getChildMenu().subscribe((r) => {
      this.menu = r.filter((x) => {
        return !x.isModule;
      });
      this.moduleMenu = r.filter((x) => {
        return x.isModule && !x.isBaseModule;
      });
      this.currentMenu = r.find(x => x.isModule && x.isBaseModule);
    });
  }

  logout() {
    this.router.navigate(['/login']);
    this.authService.logout();
  }

  public changePassword(): void {
    const dialogRef = this.dialog.open(PasswordChangeComponent, {
      panelClass: 'edit-dialog',
      width: '600px',
      data: 'changePassword',
    });
    dialogRef.afterClosed().subscribe((x) => {
      if (x as boolean) this.snackBar.showDurationSnackbar(x);
    });
  }

  openTranslate() {
    const dialogRef = this.dialog.open(PasswordChangeComponent, {
      panelClass: 'edit-dialog',
      width: '600px',
      data: 'changeLanguage',
    });
    dialogRef.afterClosed().subscribe((x) => {
      if (x as boolean) this.snackBar.showDurationSnackbar(x);
    });
  }

  public onRefresh(_event: MouseEvent) {
    if (!this.moGenerationRunning) location.reload();
  }

  public getSelectLists(): void {
    this.getLanguageList();
  }

  public getLanguageList(): void {
    this.langService.getLanguages(true, false).subscribe((r) => {
      this.languageList = r;
    });
  }

  public changeUserLang(lang) {
    this.authService.setLanguageForUser(lang)?.subscribe({
      next: (r) => {
        this.snackBar.showDurationSnackbar(new HttpErrorResponse({
          status: 400,
          error: this.translate.instant('languageChangeSuccessful')
        }));
      },
      error: (err) => {
        this.snackBar.showButtonSnackbar(err);
      }
    });
  }

  public isUpdateWarningVisible() {
    return this.showUpdateNeededWarning
  }

  public reloadPage() {
    window.location.href = window.location.href;
  }

  ngOnDestroy(): void {
    this.appVerCheckSubscription?.unsubscribe();
  }

  onThemeChanged() {
    this.themeService.isDarkMode = !this.themeService.isDarkMode;
  }
}
